import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from "@angular/core";
import {ListItem} from "../../list-item";
import {localizationKey, LocalizationStrings} from "../../../../i18n/i18n-model";
import {AuthenticatedUser} from "../../../authentication/authenticated-user";
import {emptyListItem} from "../list-item.component";
import {Observable} from "rxjs";
import {finalize, switchMap} from "rxjs/operators";
import {Notifications, NotificationType} from "../../../../tools/notifications/notifications";
import {ListItemTextBuilder} from "../../list-item-text-builder.service";
import {AssignTechnicianService} from "../../assign-technician/assign-technician.service";
import {ErrorCode, ErrorResponse, singleCodeFromResponse} from "../../../../tools/error-response";
import {DoctorAssignmentService} from "../../doctor-assignment.service";
import {ReportUploadItem} from "../../report/report-upload-item";
import {ClipboardService} from "ngx-clipboard";
import {NavigationRoute} from "../../../../tools/navigation/navigation-route";
import {RouteNavigator} from "../../../../tools/navigation/route-navigator.service";
import {CloseAnalysisService} from "../../close-analysis.service";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {FoundMeasurement} from "../../found-measurement";
import {RejectStudyService} from "../../reject/reject-study.service";
import {RestoreMeasurementService} from "../../restore-measurement/restore-measurement.service";
import {AuthenticationService} from "../../../authentication/authentication.service";
import {canMailDevice} from "./can-mail-device";
import {shouldShowMailingInfoButton} from "./should-show-mailing-info-button";
import {ReleaseToggleState} from "../../../../tools/release-toggles/release-toggle-state";
import {DeleteMeasurementService} from "../../delete-measurement/delete-measurement.service";
import {replaceAnchors} from "../../../../tools/replace-anchors/replace-anchors";
import {I18nService} from "../../../../i18n/i18n.service";
import {ActionButtonState} from "../../../../tools/confirm-action-button/pre-confirm-action-button.component";
import {RepeatMeasurementService} from "../../repeat-measurement/repeat-measurement.service";
import {canReassignMeasurement} from "../can-reassign-measurement";
import {whichDoctorTypeIsAssignable} from "./which-doctor-type-is-assignable";
import {isSelectedDoctorAssignable} from "../is-selected-doctor-assignable";
import {Doctor} from "../../doctor";
import {AnalysisService} from "../../../analize/analysis.service";
import {shouldNotifyCurrentAnalysisInProgress} from "./should-notify-current-analysis-in-progress";
import {isAnalysableType} from "./is-analysable-type";
import {canDataOfficerOpenAnalysisInStatus} from "./can-data-officer-open-analysis-in-status";
import {canDoctorAnalyseMeasurementInStatus} from "./can-doctor-analyse-measurement-in-status";
import {GetSleepReportDataService} from "../../../analize/sleep-report/get-sleep-report-data.service";
import {SleepReportGenerator} from "../../../analize/sleep-report/sleep-report-generator.service";
import {withGeneratedFindings} from "../../../analize/sleep-report/sleep-findings-text";
import {shouldShowCloseAnalysisButton} from "./should-show-close-analysis-button";


@Component({
  selector: "app-list-item-action-buttons",
  templateUrl: "./list-item-action-buttons.component.html",
  standalone: false
})
export class ListItemActionButtonsComponent implements OnInit {

  @Input() item: ListItem = emptyListItem();

  @Output() measurementChanged = new EventEmitter<void>();


  modalRefRejectConfirm: BsModalRef | undefined;
  @ViewChild("templateConfirmReject") templateConfirmReject!: TemplateRef<any>;

  modalRefDeleteMeasurementConfirm: BsModalRef | undefined;
  @ViewChild("templateConfirmDeleteMeasurement") templateConfirmDeleteMeasurement!: TemplateRef<any>;

  modalRefWarnAnalysisBeingAnalysed: BsModalRef | undefined;
  @ViewChild("templateWarnAnalysisBeingAnalysed") templateWarnAnalysisBeingAnalysed!: TemplateRef<any>;

  closeAnalysisActionState: ActionButtonState = "INITIAL";
  user!: AuthenticatedUser;
  repeatMeasurementState: ActionButtonState = "INITIAL";

  currentlyAnalysedBy: Doctor[] = [];

  constructor(
    private readonly listItemTextGenerator: ListItemTextBuilder,
    private readonly assignTechnicianService: AssignTechnicianService,
    private readonly notifications: Notifications,
    private readonly doctorAssignmentService: DoctorAssignmentService,
    private readonly clipboardService: ClipboardService,
    private readonly routeNavigator: RouteNavigator,
    private readonly closeEcgAnalysisService: CloseAnalysisService,
    private readonly modalService: BsModalService,
    private readonly rejectStudyService: RejectStudyService,
    private readonly restoreMeasurementService: RestoreMeasurementService,
    private readonly authenticationService: AuthenticationService,
    private readonly deleteMeasurementService: DeleteMeasurementService,
    private readonly i18nService: I18nService,
    private readonly repeatMeasurementService: RepeatMeasurementService,
    private readonly analysisService: AnalysisService,
    private readonly getSleepReportDataService: GetSleepReportDataService,
    private readonly sleepReportGenerator: SleepReportGenerator
  ) {
  }

  ngOnInit(): void {
    this.user = this.authenticationService.getCurrentAuthenticatedUser()!;
  }

  shouldShowAssignTechnicianButton(): Boolean {

    return whichDoctorTypeIsAssignable(this.item.foundMeasurement) === "TECHNICIAN"
      && this.user.role === "DATA_OFFICER"
      && this.item.selectedAnalyzer !== undefined;
  }

  onAssignTechnicianClick() {
    this.item.isAssignTechnicianInProgress = true;

    const patientDataLines = this.listItemTextGenerator.build(this.item.foundMeasurement).split("\n");

    this.assignTechnicianService.assign(
      {
        measurementId: this.item.foundMeasurement.id,
        technician: this.item.selectedAnalyzer!,
        patientDataLines: patientDataLines
      }
    )
      .pipe(finalize(() => this.item.isAssignTechnicianInProgress = false))
      .subscribe((newMeasurement) => {
          this.notifications.addNotification(NotificationType.OK, localizationKey("measurementGridAssignTechnicianSuccessful"));
          this.item.foundMeasurement = newMeasurement;
          this.measurementChanged.emit();
        },
        (errorResponse: ErrorResponse) =>
          this.notifications.addNotification(
            NotificationType.ERROR,
            this.getAssignTechnicianErrorMessageOrDefault(errorResponse)
          )
      );
  }

  private getAssignTechnicianErrorMessageOrDefault(errorResponse: ErrorResponse): keyof LocalizationStrings {
    const errorCode = singleCodeFromResponse(errorResponse);
    switch (errorCode) {
      case ErrorCode.MEASUREMENT_JOB_STATUS_INVALID:
        return errorCode;
      case ErrorCode.FOLDER_HAS_NOT_FINISHED_SYNCING:
        return errorCode;
      default:
        return "measurementGridAssignTechnicianError";
    }
  }

  isDoctorAssignable(): boolean {
    return this.user.role === "DATA_OFFICER"
      && whichDoctorTypeIsAssignable(this.item.foundMeasurement) === "DOCTOR"
      && isSelectedDoctorAssignable(
        this.item.selectedAnalyzer,
        this.item.foundMeasurement.doctor?.id
      );
  }

  getAssignButtonColorClass() {
    return this.item.foundMeasurement.type === "SLEEP" && this.item.foundMeasurement.status === "UPLOADED" ? "btn-success" : "btn-primary";
  }

  onAssignClick() {

    const item = this.item;

    const patientDataLines = this.listItemTextGenerator.build(item.foundMeasurement).split("\n");

    this.doctorAssignmentService.assignDoctor({
        measurementId: item.foundMeasurement.id,
        selectedAnalyzer: item.selectedAnalyzer!,
        patientDataLines: patientDataLines
      }
    )
      .subscribe(
        (newMeasurement) => {
          item.foundMeasurement = newMeasurement;
          this.measurementChanged.emit();
        },
        (errorResponse: ErrorResponse) =>
          this.notifications.addNotification(
            NotificationType.ERROR,
            this.getErrorMessageOrDefault(errorResponse)
          )
      );

  }

  private getErrorMessageOrDefault(errorResponse: ErrorResponse): keyof LocalizationStrings {
    const errorCode = singleCodeFromResponse(errorResponse);
    switch (errorCode) {
      case ErrorCode.ECG_DATA_OFFICER_FILESHARE_FOLDER_INVALID:
        return errorCode;
      case ErrorCode.DOCTOR_DOES_NOT_HAVE_ACCESS_TO_EXTERNAL_ANALYSIS:
        return "assignFailedDoctorDoesNotHaveAccessToExternalAnalysis";
      case ErrorCode.FOLDER_HAS_NOT_FINISHED_SYNCING:
        return errorCode;
      case ErrorCode.MEASUREMENT_JOB_STATUS_INVALID:
        return errorCode;
      default:
        return "assignDoctorFailed";
    }
  }

  getAssignButtonLabel(): keyof LocalizationStrings {
    return this.item.foundMeasurement.type === "SLEEP"
    && this.item.foundMeasurement.status === "UPLOADED"
      ? "measurementsGridButtonLabelAssignDoctor"
      : "measurementsGridButtonLabelAssign";
  }

  onUploadReportClick() {
    this.setShowReportUpload(true);
  }

  private setShowReportUpload(show: boolean) {
    this.item.showReportUpload = show;
  }

  onReportFileUploadCancelled() {
    this.setShowReportUpload(false);
  }

  onReportFileUploaded(reportUploadItem: ReportUploadItem) {
    this.item.foundMeasurement = reportUploadItem.measurement;
    this.setShowReportUpload(false);
    this.measurementChanged.emit();
  }

  canUploadReport(): boolean {
    return this.user.role === "DATA_OFFICER"
      && this.item.foundMeasurement.type === "SLEEP"
      && this.item.foundMeasurement.status === "READY_FOR_POST_ANALYSIS";
  }

  shouldShowCopyButton(): boolean {
    return this.user.role === "DATA_OFFICER"
      && this.item.foundMeasurement.type === "SLEEP"
      && this.item.foundMeasurement.status !== "INITIATED";
  }

  onCopyClick() {
    const text = this.listItemTextGenerator.build(this.item.foundMeasurement);
    this.clipboardService.copy(text);
  }

  shouldShowOpenAnalysisButton(): boolean {
    const status = this.item.foundMeasurement.status;
    if (this.user.role === "DATA_OFFICER") {
      return isAnalysableType(this.item.foundMeasurement.type)
        && canDataOfficerOpenAnalysisInStatus(status);
    }

    if (this.user.role === "DOCTOR") {
      return isAnalysableType(this.item.foundMeasurement.type)
        && canDoctorAnalyseMeasurementInStatus({
          status: status,
          isGlobalTechnician: this.user.isGlobalTechnician,
          preAnalysisAssignType: this.item.foundMeasurement.technicianAssignType,
          analysisAssignType: this.item.foundMeasurement.assignType,
        });
    }

    return false;
  }

  onOpenAnalysisClick() {
    switch (this.item.foundMeasurement.type) {
      case "HOLTER_ECG":
      case "SYMPTOM_HOLTER":
      case "ECG_ATRIAL_FIBRILLATION":

        if (shouldNotifyCurrentAnalysisInProgress({
          status: this.item.foundMeasurement.status,
          analysisAssignType: this.item.foundMeasurement.assignType,
          preanalysisAssignType: this.item.foundMeasurement.technicianAssignType
        })) {
          this.warnUserAnalysisInProgress();
        } else {
          this.openEcgAnalysisPage();
        }

        break;
      case "BLOOD_PRESSURE":
        this.routeNavigator
          .navigateToPath([NavigationRoute.ANALYSIS_BLOOD_PRESSURE, this.item.foundMeasurement.id.toString()]);
        break;
      case "SLEEP":
        this.routeNavigator.navigateToPath([NavigationRoute.ANALYSIS_SLEEP, this.item.foundMeasurement.id.toString()]);
        break;
    }
  }

  private warnUserAnalysisInProgress() {
    this.item.isOpenAnalysisInProgress = true;
    this
      .analysisService
      .getAnalysers(this.item.foundMeasurement.id)
      .pipe(
        finalize(() =>
          this.item.isOpenAnalysisInProgress = false
        )
      )
      .subscribe({
        next: (doctors) => {
          this.currentlyAnalysedBy = doctors;

          const isBeingAnalysed = this.currentlyAnalysedBy.length > 0;
          this.item.foundMeasurement = {
            ...this.item.foundMeasurement,
            currentlyAnalysed: isBeingAnalysed
          };
          if (isBeingAnalysed) {
            this.modalRefWarnAnalysisBeingAnalysed = this.modalService.show(this.templateWarnAnalysisBeingAnalysed);
          } else {
            this.openEcgAnalysisPage();
          }
        },
        error: () => {
          this.openEcgAnalysisPage();
          this.notifications.addNotification(
            NotificationType.WARNING,
            localizationKey("measurementsGridWarningOpeningAnalysisThatIsBeingAnalysed")
          );
        }
      });
  }

  openEcgAnalysisPage() {
    this.modalRefWarnAnalysisBeingAnalysed?.hide();

    this.routeNavigator.navigateToPath([NavigationRoute.ANALYSIS_ECG, this.item.foundMeasurement.id.toString()]);
  }

  shouldShowCloseAnalysisButton(): boolean {
    const measurement = this.item.foundMeasurement;
    return shouldShowCloseAnalysisButton({
      role: this.user.role,
      type: measurement.type,
      status: measurement.status,
      feature569Released: this.feature569Released()
    });
  }

  private feature569Released(): boolean {
    return ReleaseToggleState.getInstance().isReleased("AUTOMATICALLY_GENERATE_SLEEP_REPORT_569");
  }

  onCloseAnalysisClick() {
    if (this.closeAnalysisActionState === "INITIAL") {
      this.closeAnalysisActionState = "CONFIRMATION";
      return;
    }

    this.closeAnalysisActionState = "ACTION_IN_PROGRESS";
    this.item.isCloseAnalysisInProgress = true;

    this.closeAnalysis()
      .pipe(finalize(() => (this.item.isCloseAnalysisInProgress = false)))
      .subscribe(
        (newMeasurement) => {
          this.item.foundMeasurement = newMeasurement;
          this.measurementChanged.emit();
        },
        () => {
          this.notifications.addNotification(NotificationType.ERROR, localizationKey("closeAnalysisFailed"));
          this.closeAnalysisActionState = "INITIAL";
        }
      );
  }

  private closeAnalysis(): Observable<FoundMeasurement> {
    const measurement = this.item.foundMeasurement;

    if (measurement.type === "SLEEP" && !measurement.reportExists) {
      return this.generateAndUploadSleepReport(measurement.id);
    }

    return this.closeEcgAnalysisService.closeAnalysis({measurementId: measurement.id, report: undefined});
  }

  private generateAndUploadSleepReport(measurementId: number): Observable<FoundMeasurement> {
    return this.getSleepReportDataService
      .get(measurementId)
      .pipe(
        switchMap(sleepReportData => this.sleepReportGenerator.generate(withGeneratedFindings(
          sleepReportData,
          key => this.i18nService.getLocalizedString(key, sleepReportData.metadata.reportLanguage)
        ))),
        switchMap(reportFile => this.closeEcgAnalysisService.closeAnalysis({measurementId: measurementId, report: reportFile}))
      );
  }

  onDeleteMeasurementClick() {
    this.modalRefDeleteMeasurementConfirm = this.modalService.show(this.templateConfirmDeleteMeasurement, {
      class: "modal-sm",
      initialState: this.item.foundMeasurement
    });
  }

  generateDeleteMeasurementConfirmMessage() {
    return replaceAnchors(
      this.i18nService
        .getLocalizedString("confirmDeleteMeasurement"),
      {code: this.item.foundMeasurement.code});
  }

  deleteMeasurement() {
    this.item.isDeleteMeasurementInProgress = true;
    this.deleteMeasurementService.deleteInitiatedMeasurement(this.item.foundMeasurement.id)
      .pipe(finalize(() => (this.item.isDeleteMeasurementInProgress = false)))
      .subscribe((newMeasurement) => {
        this.item.foundMeasurement = newMeasurement;
        if (newMeasurement.deleted) {
          this.measurementChanged.emit();
        }
      }, () => {
        this.notifications.addNotification(NotificationType.ERROR, localizationKey("deletingMeasurementFailed"));
      });
    this.modalRefDeleteMeasurementConfirm!.hide();
  }

  shouldShowDeleteButton() {
    return (
        this.user.role === "DATA_OFFICER"
        || this.user.role === "NURSE"
      )
      && this.item.foundMeasurement.status === "INITIATED"
      && !this.item.foundMeasurement.deleted
      && (
        this.item.foundMeasurement.homeStatus === undefined
        || this.item.foundMeasurement.homeStatus === "WAITING_FOR_MAILING_INFO"
      );
  }

  onReturnStudy(returnedStudy: FoundMeasurement): void {
    this.item.foundMeasurement = returnedStudy;
    this.measurementChanged.emit();
  }

  shouldShowReturnStudy(): boolean {
    return this.user.role === "DATA_OFFICER" && this.item.foundMeasurement.status === "READY_FOR_POST_ANALYSIS";
  }

  canRejectStudy(): boolean {
    return this.user.role === "DATA_OFFICER"
      && this.item.foundMeasurement.status !== "DONE"
      && this.item.foundMeasurement.status !== "REJECTED_BY_DATA_OFFICER"
      && this.item.foundMeasurement.status !== "INITIATED"
      && !ReleaseToggleState.getInstance().isReleased("REJECT_MEASUREMENT_IN_MEASUREMENT_DETAILS_516");
  }

  onRejectStudyClick(): void {
    this.modalRefRejectConfirm = this.modalService.show(this.templateConfirmReject, {
      class: "modal-sm",
      initialState: this.item.foundMeasurement
    });
  }

  rejectStudy(): void {

    this.modalRefRejectConfirm?.hide();

    if (!this.canRejectStudy()) {
      return;
    }

    this.item.isRejectStudyInProgress = true;

    this.rejectStudyService
      .reject(this.item.foundMeasurement.id)
      .pipe(finalize(() => (this.item.isRejectStudyInProgress = false)))
      .subscribe(
        (newMeasurement) => {
          this.item.foundMeasurement = newMeasurement;
          this.measurementChanged.emit();
        },
        () => this.notifications.addNotification(NotificationType.ERROR, localizationKey("rejectStudyFailed"))
      );
  }

  shouldShowRestoreMeasurementButton() {
    return this.item.foundMeasurement.status === "INITIATED" && this.item.foundMeasurement.deleted;
  }

  restoreMeasurement() {
    this.item.isRestoreMeasurementInProgress = true;
    this.restoreMeasurementService
      .restore(this.item.foundMeasurement.id)
      .pipe(finalize(() => this.item.isRestoreMeasurementInProgress = false))
      .subscribe((newMeasurement) => {
          this.notifications.addNotification(NotificationType.OK, localizationKey("restoreMeasurementSucceeded"));
          this.item.foundMeasurement = newMeasurement;
          this.measurementChanged.emit();
        },
        () => this.notifications.addNotification(NotificationType.ERROR, localizationKey("restoreMeasurementFailed")));
  }

  canMailDevice() {
    return canMailDevice(this.user.role, this.item.foundMeasurement.homeStatus);
  }

  mailDevice() {
    this.routeNavigator.navigateToPath([NavigationRoute.MAIL_DEVICE, this.item.foundMeasurement.id.toString()]);
  }

  shouldShowMailingInfo() {
    return shouldShowMailingInfoButton(
      this.user.role,
      this.item.foundMeasurement.homeStatus,
      this.item.foundMeasurement.status
    );
  }

  shouldShowReassignButton() {
    return this.user.role === "DATA_OFFICER"
      && canReassignMeasurement(this.item.foundMeasurement);
  }

  onReassignCompleted(measurement: FoundMeasurement) {
    this.item.foundMeasurement = measurement;
    this.measurementChanged.emit();
  }

  canRepeatMeasurement(): boolean {
    return this.user.role === "HMO"
      && this.item.foundMeasurement.homeStatus !== undefined
      && ["UPLOADED", "READY_FOR_ANALYSIS", "INITIATED"].includes(this.item.foundMeasurement.status)
      && ["MAILED_TO_PATIENT", "RETURNED_FROM_PATIENT"].includes(this.item.foundMeasurement.homeStatus);
  }

  repeatMeasurement() {

    if (this.repeatMeasurementState === "INITIAL") {
      this.repeatMeasurementState = "CONFIRMATION";
      return;
    }

    this.repeatMeasurementState = "ACTION_IN_PROGRESS";
    this.repeatMeasurementService.repeat(this.item.foundMeasurement.id)
      .pipe(finalize(() => this.repeatMeasurementState = "INITIAL"))
      .subscribe({
        next: (foundMeasurement: FoundMeasurement) => {
          this.item.foundMeasurement = foundMeasurement;
        },
        error: () => {
          this.notifications.addNotification(NotificationType.ERROR, localizationKey("repeatMeasurementFailed"));
        }
      });
  }

  shouldShowCurrentlyAnalysedIndicator() {
    return this.item.foundMeasurement.currentlyAnalysed
      && shouldNotifyCurrentAnalysisInProgress({
        status: this.item.foundMeasurement.status,
        analysisAssignType: this.item.foundMeasurement.assignType,
        preanalysisAssignType: this.item.foundMeasurement.technicianAssignType
      });

  }
}
